import { FC, type FormEvent, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { useAv } from "../../../../utils/av";
import {
  DEFAULT_BREAKOUT_DURATION,
  MIN_GROUP_COUNT,
  useBreakouts,
} from "../../../../utils/breakouts";
import { Link } from "../../../../utils/routing/components/Link";
import { pageUrl } from "../../../../utils/urls";

import { BreakoutDurationField } from "components/forms/BreakoutDurationField";
import { Trans, t } from "i18n/i18n";
import { Card } from "mds/components/Card";
import { ToggleSwitch } from "mds/components/ToggleSwitch";
import { Tooltip } from "mds/components/Tooltip";
import { TooltipTrigger } from "mds/components/TooltipTrigger";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { RefreshIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import {
  pageSelectors,
  selectActiveClassSession,
  selectCurrentSectionIsUnlinked,
} from "store/selectors";
import { selectIsInBreakout } from "store/selectors/breakouts";
import { useObjectSelector } from "store/store-hooks";

interface GroupConfigBreakoutsBannerProps {
  pageId: string;
  hasAssessments: boolean;
}

export const GroupConfigBreakoutsBanner: FC<GroupConfigBreakoutsBannerProps> = ({
  pageId,
  hasAssessments,
}) => {
  const { isInVirtualClass, isVirtualClassActive } = useAv();
  const isInBreakout = useAppSelector(selectIsInBreakout);
  const { onStartBreakouts } = useBreakouts();
  const isDefaultSection = useAppSelector(selectCurrentSectionIsUnlinked);
  const form = useForm({ defaultValues: { breakout_duration: DEFAULT_BREAKOUT_DURATION } });

  // When the page has assessments, sync must stay off; otherwise keep the user's toggle.
  const [userWantsSync, setUserWantsSync] = useState(true);
  const shouldSyncBreakoutsWithPageGroups = !hasAssessments && userWantsSync;
  const classSession = useObjectSelector(selectActiveClassSession, [
    "breakout_session_synced_page_id",
  ]);

  const syncedPage = useObjectSelector(
    (s) => pageSelectors.selectById(s, classSession?.breakout_session_synced_page_id),
    ["id", "title", "section_id"],
  );

  const onStartBreakoutsFromGroupConfig = (durationMinutes: number) => {
    onStartBreakouts(
      "page",
      durationMinutes,
      pageId,
      MIN_GROUP_COUNT,
      shouldSyncBreakoutsWithPageGroups,
    );
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    if (!isDefaultSection) {
      event.preventDefault();
      onStartBreakoutsFromGroupConfig(DEFAULT_BREAKOUT_DURATION);
      return;
    }
    form.handleSubmit(({ breakout_duration }) =>
      onStartBreakoutsFromGroupConfig(Number(breakout_duration)),
    )(event);
  };

  if (
    !classSession ||
    !isInVirtualClass ||
    !isVirtualClassActive ||
    (isInBreakout && !classSession.breakout_session_synced_page_id)
  )
    return null;

  if (isInBreakout) {
    if (classSession?.breakout_session_synced_page_id === pageId) {
      return (
        <Card className="flex w-full flex-wrap items-center justify-between">
          <div className="flex items-center gap-1 text-sm font-medium text-black-tint-20">
            <RefreshIcon />
            {t("group_configuration_card.breakouts_synced_with_this_page")}
          </div>
          <TooltipTrigger
            content={t("group_configuration_card.breakouts_synced_with_this_page_tooltip")}
          />
        </Card>
      );
    }

    if (classSession.breakout_session_synced_page_id && !!syncedPage) {
      return (
        <Card className="flex w-full items-center justify-between">
          <div className="flex items-center gap-1 truncate text-sm font-medium text-black-tint-20">
            <RefreshIcon />
            <Trans
              i18nKey="group_configuration_card.breakouts_synced_with_another_page"
              t={t}
              values={{ title: syncedPage.title }}
            >
              <Link
                className="truncate text-blue"
                to={pageUrl(syncedPage.section_id, syncedPage.id)}
              >
                {syncedPage.title}
              </Link>
            </Trans>
          </div>
          <TooltipTrigger
            content={t("group_configuration_card.breakouts_synced_with_another_page_tooltip")}
          />
        </Card>
      );
    }
  }

  if (!isInBreakout) {
    return (
      <Card
        className={
          isDefaultSection
            ? "w-full @container/breakout-config"
            : "flex w-full flex-wrap items-center justify-between"
        }
      >
        <FormProvider {...form}>
          <form
            className={
              isDefaultSection
                ? "flex flex-col gap-4 @[560px]/breakout-config:flex-row @[560px]/breakout-config:items-start @[560px]/breakout-config:justify-between"
                : "flex w-full flex-wrap items-center justify-between"
            }
            noValidate
            onSubmit={onSubmit}
          >
            <div
              className={
                isDefaultSection
                  ? "flex w-full flex-col gap-4 @[560px]/breakout-config:w-1/2 @[560px]/breakout-config:min-w-[20rem]"
                  : undefined
              }
            >
              <Tooltip
                content={
                  hasAssessments
                    ? t("group_configuration_card.breakouts_synced_disabled_tooltip")
                    : undefined
                }
              >
                <ToggleSwitch
                  className={
                    isDefaultSection ? "min-h-8 w-full justify-between text-left" : undefined
                  }
                  disabled={hasAssessments}
                  eventName="Group Settings - Keep breakouts in sync"
                  isSelected={shouldSyncBreakoutsWithPageGroups}
                  labelStart={
                    <div className="flex items-center gap-1 text-sm font-medium text-black-tint-20">
                      <RefreshIcon />
                      {t("group_configuration_card.keep_groups_and_breakouts_in_sync")}
                    </div>
                  }
                  onClick={() => setUserWantsSync(!userWantsSync)}
                />
              </Tooltip>
              {isDefaultSection && <BreakoutDurationField layout="inline" />}
            </div>
            <SquareButton
              className={
                isDefaultSection
                  ? "shrink-0 self-end @[560px]/breakout-config:self-start"
                  : undefined
              }
              eventName="Group Settings - Start breakouts"
              kind="accent"
              size="s"
              type="submit"
            >
              {t("breakout_group_modal.start_breakouts")}
            </SquareButton>
          </form>
        </FormProvider>
      </Card>
    );
  }

  return null;
};
